import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';
import { onboardingTasks } from '@ourbranch/onboarding-tasks';

import { AuthContext } from 'core/components/auth';
import { Label } from 'core/components/label';
import { brandConstants } from 'brand-config';
import { useStore } from 'core/store/store.mobx';
import { dateTimeFormatter } from 'core/helpers/formatters';
import { Loading, SelectableRow, Table } from 'core';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { useToast } from 'core/components/toast';
import viewIcon from '../../../../assets/view.svg';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import editIcon from 'core/assets/svg/edit.svg';
import { CopyInternalDocToPolicyDocsModal } from '../copy-internal-doc-to-policy-docs-modal';
import { ConfirmDeleteInternalDocumentModal } from '../confirm-delete-internal-document-modal';
import { RenameDocumentModal } from 'core/components/rename-document-modal';
import { DropzoneContainer } from 'core/components/dropzone';
import useStyles from '../../documents.styles';

const InternalDocuments = observer(
  ({
    fetchDocuments,
    sortDocuments,
    getDocName,
    downloadDocument,
    uploaded,
    uploadFile,
    percent,
    error,
    deleting,
    file
  }) => {
    const classes = useStyles();
    const toast = useToast();
    const [copyDocsModalOpen, setCopyDocsModalOpen] = useState(false);
    const [documentToCopy] = useState(null);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [documentToRename, setDocumentToRename] = useState(null);

    const {
      account: {
        id: accountId,
        policies: { documents, loadingDocuments: loading, deleteInternalDocument, setDocumentLabel },
        tasks
      }
    } = useStore();
    const { canUploadDocuments, canDeleteInternalDocuments } = useContext(AuthContext);

    const onRenameConfirm = useCallback(
      async (label) => {
        if (!documentToRename) {
          return;
        }
        try {
          await setDocumentLabel(accountId, documentToRename.path, label);
        } catch {
          toast.notify({
            type: 'error',
            message: `Couldn't rename ${getDocName(documentToRename.path)}`
          });
          throw new Error('rename failed');
        }
        // Re-fetch separately so a fetch failure isn't misreported as a rename failure.
        await fetchDocuments();
        toast.notify({
          type: 'success',
          message: 'Document renamed successfully'
        });
      },
      [accountId, setDocumentLabel, documentToRename, fetchDocuments, getDocName, toast]
    );

    const onDeleteConfirm = useCallback(async () => {
      if (!documentToDelete) {
        return;
      }
      try {
        await deleteInternalDocument(accountId, documentToDelete.path);
        await fetchDocuments();
        setDocumentToDelete(null);
      } catch {
        toast.notify({
          type: 'error',
          message: `Couldn't delete ${getDocName(documentToDelete.path)}`
        });
        setDocumentToDelete(null);
      }
    }, [accountId, deleteInternalDocument, documentToDelete, fetchDocuments, getDocName, toast]);

    const documentToTaskMap = new Map();
    if (tasks && tasks.length > 0) {
      tasks.forEach((task) => {
        if (task.data) {
          const parsedData = typeof task.data === 'string' ? JSON.parse(task.data) : task.data;

          if (parsedData?.files) {
            parsedData.files.forEach((file) => {
              if (file?.documentUrl) {
                documentToTaskMap.set(file.documentUrl, task.taskName);
              }
            });
          }

          // Backward compatibility - support old single documentUrl field for tasks that will not
          // have a files array but still the doc will need to show
          if (parsedData?.documentUrl) {
            if (!documentToTaskMap.has(parsedData.documentUrl)) {
              documentToTaskMap.set(parsedData.documentUrl, task.taskName);
            }
          }
        }
      });
    }

    const getTaskName = useCallback(
      (documentPath) => {
        const mappedTaskName = documentToTaskMap.get(documentPath);
        if (mappedTaskName) {
          return mappedTaskName;
        }

        // special case for spp standalone task
        if (documentPath?.includes('inventory_') && documentPath?.endsWith('_spp.pdf')) {
          return onboardingTasks.NeedScheduledPersonalPropertyImages;
        }

        return 'Not specified';
      },
      [documentToTaskMap]
    );

    return (
      <>
        <Section
          title="Internal Documents"
          rightLabel="Total"
          rightValue={`${loading || !documents ? '...' : documents.internal.length} documents`}
        >
          {loading || !documents ? (
            <Loading />
          ) : (
            <Card>
              {documents.internal.length || uploaded.length ? (
                <Table
                  selfContained={false}
                  header={
                    <>
                      <TableCell className={classes.fileNameHeader}>FILE NAME</TableCell>
                      <TableCell className={classes.documentTypeHeader}>DOCUMENT TYPE</TableCell>
                      <TableCell className={classes.lastUpdateHeader}>LAST UPDATE</TableCell>
                      {/* need the following empty tablecell header for the view or copy action buttons */}
                      <TableCell />
                    </>
                  }
                  body={
                    <>
                      {sortDocuments(documents.internal.slice() || []).map((d) => {
                        const fileName = getDocName(d.path);
                        const displayName = d.displayName || fileName;
                        const taskName = getTaskName(d.path);
                        // Only operator uploads (internal/ namespace) are relabelable; task docs
                        // surfaced here via other paths are rejected by the backend.
                        const isRelabelable = d.path.startsWith('internal/');
                        return (
                          <TableRow key={d.path} id={d.path}>
                            <TableCell className={classes.docTitleCell}>
                              <Tooltip title={fileName} placement="top">
                                <span className={classes.truncatedText}>{displayName}</span>
                              </Tooltip>
                              {d.displayName ? (
                                <span className={classNames(classes.truncatedText, classes.notSpecifiedText)}>
                                  {fileName}
                                </span>
                              ) : null}
                            </TableCell>
                            <TableCell className={classes.documentTypeCell}>
                              <Tooltip title={taskName} placement="top">
                                <span
                                  className={classNames(classes.truncatedText, {
                                    [classes.notSpecifiedText]: taskName === 'Not specified'
                                  })}
                                >
                                  {taskName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.lastUpdateCell}>
                              <span className={classes.smallText}>
                                {dateTimeFormatter(d.lastModified).split(' @ ')[0]}
                                <br />@ {dateTimeFormatter(d.lastModified).split(' @ ')[1]}
                              </span>
                            </TableCell>
                            <TableCell>
                              <IconButtonWithTooltip
                                tooltip="View file"
                                onClick={() => downloadDocument(d.path)}
                                icon={viewIcon}
                                alt="view document"
                              />
                              {canUploadDocuments && isRelabelable && (
                                <IconButtonWithTooltip
                                  tooltip="Rename file"
                                  onClick={() => setDocumentToRename(d)}
                                  icon={editIcon}
                                  alt="rename document"
                                />
                              )}
                              {canDeleteInternalDocuments && (
                                <IconButtonWithTooltip
                                  tooltip="Delete file"
                                  onClick={() => setDocumentToDelete(d)}
                                  icon={deleteIcon}
                                  alt="delete document"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {sortDocuments(uploaded).map((d) => {
                        const fileName = getDocName(d.path);
                        const taskName = getTaskName(d.path);
                        return (
                          <SelectableRow key={d.path} id={d.path} onClick={downloadDocument}>
                            <TableCell className={classNames(classes.highlighted, classes.docTitleCell)}>
                              <Tooltip title={fileName} placement="top">
                                <span className={classes.truncatedText}>{fileName}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classNames(classes.highlighted, classes.documentTypeCell)}>
                              <Tooltip title={taskName} placement="top">
                                <span
                                  className={classNames(classes.truncatedText, {
                                    [classes.notSpecifiedText]: taskName === 'Not specified'
                                  })}
                                >
                                  {taskName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classNames(classes.highlighted, classes.lastUpdateCell)}>
                              <span className={classes.smallText}>
                                {dateTimeFormatter(d.lastModified).split(' @ ')[0]}
                                <br />@ {dateTimeFormatter(d.lastModified).split(' @ ')[1]}
                              </span>
                            </TableCell>
                          </SelectableRow>
                        );
                      })}
                    </>
                  }
                />
              ) : (
                <p className={classes.noDocs}>No internal documents for this member.</p>
              )}
              {canUploadDocuments ? (
                <DropzoneContainer
                  onUpload={uploadFile}
                  progress={percent}
                  error={error}
                  deleting={deleting}
                  uploading={file ? file.path : undefined}
                />
              ) : (
                <div className={classes.cantUploadContainer}>
                  <Label className={classes.cantUpload}>
                    To provide a document for this member, please send the document to Branch at{' '}
                    <a
                      className={classes.support}
                      href={`mailto:${brandConstants.SUPPORT_EMAIL}`}
                      rel="noopener noreferrer"
                    >
                      {brandConstants.SUPPORT_EMAIL}
                    </a>
                  </Label>
                </div>
              )}
            </Card>
          )}
        </Section>
        {copyDocsModalOpen && (
          <CopyInternalDocToPolicyDocsModal
            open={copyDocsModalOpen}
            document={documentToCopy}
            refetchDocuments={fetchDocuments}
            onClose={() => {
              setCopyDocsModalOpen(false);
            }}
          />
        )}
        {documentToDelete && (
          <ConfirmDeleteInternalDocumentModal
            fileName={getDocName(documentToDelete.path)}
            onConfirm={onDeleteConfirm}
            onClose={() => setDocumentToDelete(null)}
          />
        )}
        {documentToRename && (
          <RenameDocumentModal
            fileName={getDocName(documentToRename.path)}
            currentLabel={documentToRename.displayName || ''}
            onSave={onRenameConfirm}
            onClose={() => setDocumentToRename(null)}
          />
        )}
      </>
    );
  }
);

InternalDocuments.propTypes = {
  sortDocuments: PropTypes.func.isRequired,
  fetchDocuments: PropTypes.func.isRequired,
  getDocName: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
  uploaded: PropTypes.array,
  uploadFile: PropTypes.func.isRequired,
  percent: PropTypes.number,
  error: PropTypes.string,
  deleting: PropTypes.bool,
  file: PropTypes.object
};

InternalDocuments.defaultProps = {
  percent: 0,
  uploaded: [],
  deleting: false,
  error: undefined,
  file: undefined
};

export default InternalDocuments;
